import './search.scss';

import Autocomplete from '@trevoreyre/autocomplete-js';
import { createElementFromHTML, dispatchForCode, getHeaderHeight, getOffset, smoothScrollTo, getFormData } from '../../utilities/js/helper';
import AjaxLoad from '../../utilities/js/ajax-load';
import { Pagination } from '../../components/pagination/pagination';

class Search {
    constructor (element) {
        this.$search = element;
        this.$form = this.$search.querySelector('[data-search="form"]');
        this.$searchInput = this.$form.querySelector('[data-search="autocomplete"]');
        this.$searchInputWrapper = this.$form.querySelector('[data-search="inputwrapper"]');
        this.$searchSortWrapper = this.$form.querySelector('[data-search="sort"]');
        this.$deleteInputButton = this.$form.querySelector('[data-search-delete-input]');
        this.$submitButton = this.$form.querySelector('button[type="submit"]');
        this.$noResultsWrapper = document.getElementById('autocomplete-status');
        this.$noResultsContent = this.$noResultsWrapper.querySelector('[data-search="no-result"]');
        this.autocompleteField = null;
        this.$$filter = this.$form.querySelectorAll('[data-search="filter"]');
        this.autoSuggestURL = this.$form.getAttribute('data-search-auto-suggest-url');
        this.searchNoResultLabel = this.$form.getAttribute('data-search-no-results-label');

        this.$results = this.$search.querySelector('[data-search="results"]');
        this.minChars = 2;
        this.allowHistory = true;

        this.ajaxURL = null;
        this.ajaxActive = false;
        this.waitTimer = null;

        this.$$filterCount = {
            default: this.$form.querySelector('[data-search-count="default"]'),
            travels: this.$form.querySelector('[data-search-count="travels"]'),
            press_release: this.$form.querySelector('[data-search-count="press_release"]'),
            download: this.$form.querySelector('[data-search-count="download"]')
        };

        this.pagination = new Pagination(this.$results, {
            paginationAttr: '[data-search="pagination"]',
            selectPaginationAttr: '[data-search="select-pagination"]'
        });
    }

    initialize () {
        this.setEvents();
        this.initAutoComplete();
        this.pagination.check((action) => {
            this.ajaxURL = action;
            this.checkBeforeSubmit();
        });
    }

    setEvents () {
        this.$form.addEventListener('submit', (e) => {
            this.setFilter(() => {
                this.checkBeforeSubmit(0, e);
            });
        });

        this.$submitButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.setFilter(() => {
                this.checkBeforeSubmit();
            });
        });

        this.$searchSortWrapper.querySelector('select')?.addEventListener('change', () => {
            this.setFilter(() => {
                this.checkBeforeSubmit();
            });
        });

        if (this.$searchInput.value) {
            if (!this.$searchSortWrapper.classList.contains('is--visible')) {
                this.$searchSortWrapper.classList.add('is--visible');
            }
        }

        this.$searchInput.addEventListener('input', () => {
            if (this.$searchInput.value !== '') {
                this.$deleteInputButton.classList.add('is--visible');

                if (this.$searchInput.value.length < this.minChars) {
                    this.$submitButton.disabled = true;
                    this.$searchInput.classList.remove('has--error');
                }

                if (this.$searchInput.value.length >= this.minChars) {
                    this.$submitButton.disabled = false;
                }
            } else {
                this.$deleteInputButton.classList.remove('is--visible');
            }
        });

        this.$searchInput.addEventListener('keydown', (e) => {
            dispatchForCode(e, (code) => {
                if (code === 'Enter' && this.$submitButton.disabled === false) {
                    e.preventDefault();
                    this.setFilter(() => {
                        this.checkBeforeSubmit(0, e);
                    });
                }
            });
        });

        this.$deleteInputButton.addEventListener('click', () => {
            this.$searchInput.value = '';
            this.$searchInput.classList.remove('has--error');
            this.$deleteInputButton.classList.remove('is--visible');
            this.$submitButton.disabled = true;
        });

        if (history.state !== null) {
            if (history.state.type === 'ajax') {
                this.allowHistory = false;
                this.setHistoryData(history.state);
            }
        }

        // History Event Listener
        window.onpopstate = (e) => {
            if (e.state !== null) {
                if (e.state.type === 'ajax') {
                    this.allowHistory = false;
                    this.setHistoryData(e.state);
                }
            } else {
                location.replace(window.location.href);
            }
        };

        this.$$filter.forEach(($filter) => {
            $filter.addEventListener('change', (e) => {
                this.setFilter(() => {
                    this.checkBeforeSubmit(0, e);
                });
            });
        });
    }

    setFilter (callback) {
        let filter = '';

        this.$form.querySelectorAll('[data-search="filter"]:checked')?.forEach(($filter) => {
            if (filter === '') {
                filter += $filter.getAttribute('name');
            } else {
                filter += '|' + $filter.getAttribute('name');
            }
        });

        this.ajaxURL = this.$form.getAttribute('action') + '?q=' + this.$searchInput.value + '&sort=' + this.$searchSortWrapper.querySelector('select').value + ((filter !== '') ? ('&filter=' + filter) : '');

        if (typeof callback === 'function') {
            return callback();
        }
    }

    checkBeforeSubmit (timer = 0, event) {
        if (this.$searchInput.value.length < this.minChars && event) {
            event.preventDefault();
        } else {
            clearTimeout(this.waitTimer);

            if (this.ajaxActive === false && this.ajaxURL != null) {
                this.waitTimer = setTimeout(() => {
                    this.$results.appendChild(createElementFromHTML(window.OPTIONS.loader));
                    this.sendRequest();
                    this.ajaxActive = true;
                }, timer);
            } else {
                console.log('Please wait.....Ajax is running');
            }
        }
    }

    sendRequest () {
        this.result = new AjaxLoad(this.ajaxURL, {
            requestMethod: 'POST',
            requestData: new FormData(this.$form),
            onSuccess: (resultText) => {
                const results = JSON.parse(resultText);

                if (results.html) {
                    if (!this.$searchSortWrapper.classList.contains('is--visible')) {
                        this.$searchSortWrapper.classList.add('is--visible');
                    }

                    if (this.allowHistory) {
                        this.setHistory();
                    }

                    this.allowHistory = true;
                    this.setResult(results);
                } else {
                    if (results.redirect) {
                        window.location.href = results.redirect;
                    }
                }
            },
            onError: (error) => {
                console.warn(error);
            }
        });
    }

    initAutoComplete () {
        const showNoResultsMessage = () => {
            if (this.$noResultsContent) {
                this.$noResultsWrapper.classList.remove('autocomplete-no-results--hidden');
                this.$noResultsContent.textContent = this.$noResultsWrapper.dataset.searchNoResultTrans;
            }
        };
        const clearStatusMessage = () => {
            if (this.$noResultsContent) {
                this.$noResultsWrapper.classList.add('autocomplete-no-results--hidden');
                this.$noResultsContent.textContent = '';
            }
        };

        new Autocomplete(this.$searchInputWrapper, { // eslint-disable-line
            search: (input) => {
                const url = this.autoSuggestURL + '?q=' + this.$searchInput.value;
                clearStatusMessage();

                return new Promise(resolve => {
                    if (input.length < 2) {
                        return resolve([]);
                    }

                    fetch(url)
                        .then(response => response.json())
                        .then((data) => {
                            if (data.result && data.result.length > 0) {
                                const results = data.result.map((item) => ({ title: item }));
                                resolve(results);
                            } else {
                                showNoResultsMessage();
                                resolve([]);
                            }
                        });
                });
            },
            getResultValue: (result) => result.title,
            renderResult: (result, props) => {
                const regex = new RegExp(`(${this.$searchInput.value})`, 'gi');
                const highlightedTitle = result.title.replace(regex, '<strong>$1</strong>');

                return `<li ${props}>${highlightedTitle}</li>`;
            },
            onSubmit: (result) => {
                if (result) {
                    window.location.href = `/suche?q=${result.title}`;
                }
            }
        });

        this.$searchInput.removeAttribute('aria-activedescendant');

        this.$searchInput.addEventListener('blur', () => {
            this.$searchInput.removeAttribute('aria-activedescendant');
        });
    }

    setResult (results) {
        this.ajaxActive = false;
        const $result = createElementFromHTML(results.html);
        this.$results.innerHTML = '';

        if ($result && $result.length > 1) {
            $result.forEach((item) => {
                this.$results.appendChild(item);
            });
        } else if ($result) {
            this.$results.appendChild($result);
        }

        this.setFilterCount(results);

        const targetScrollPos = getOffset(this.$searchSortWrapper).top - getHeaderHeight();
        smoothScrollTo(0, targetScrollPos, 500);

        this.pagination.check((action) => {
            this.ajaxURL = action;
            this.checkBeforeSubmit();
        });
    }

    setFilterCount (results) {
        if (typeof results.counts === 'object') {
            for (const key in results.counts) {
                this.$$filterCount[key].innerHTML = results.counts[key];
            }
        }
    }

    setHistory () {
        history.pushState(getFormData(this.$form, 'object'), '', this.ajaxURL);
    }

    setHistoryData (data) {
        for (const key in data) {
            const $domElement = this.$form.querySelector('[name="' + key + '"]');

            if ($domElement) {
                if ($domElement.nodeName === 'INPUT') {
                    $domElement.value = data[key];
                }
            }
        }

        this.checkBeforeSubmit();
    }
}

export { Search };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $searchPage = $context.querySelector('[data-search="root"]');

        if ($searchPage) {
            const $search = new Search($searchPage);
            $search.initialize();
        }
    }
});
